import { FireLifecycleCard } from '../../components/FireLifecycle';
import { lifecycleTitle, usesRetirementConfidence } from '@perdiem/trpc/fire-lifecycle';
import { ScrollView, View, Text, RefreshControl, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useState, useCallback, useMemo } from 'react';
import { trpc } from '../../lib/trpc';
import {
  Card,
  StatCard,
  SectionHeader,
  LoadingSpinner,
  ErrorState,
  ConnectionErrorBanner,
  SegmentedControl,
  PageContainer,
  PageHeader,
  WebHead,
  PerDiemCard,
  DashboardGreeting,
  Money,
} from '../../components';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useColors } from '../../lib/colors';
import { useFireImpact } from '../../hooks/useFireImpact';
import { formatFiAgeSummary } from '../../lib/fire-summary';
import {
  formatCurrency,
  formatDate,
  formatCurrencyChange,
  formatSharePrice,
  formatSignedPercent,
} from '../../lib/utils';
import {
  aggregatePortfolioHoldingsBySymbol,
  filterDailyMarketHoldings,
  sortPortfolioHoldingAggregates,
} from '../../lib/portfolio-holdings';
import { useScreenReadyTelemetry } from '../../hooks/useScreenReadyTelemetry';

type HoldingsWidgetView = 'largest' | 'movers';

type DashboardProfile = {
  id: string;
  type: 'primary' | 'spouse';
  isSelf?: boolean;
  firstName: string | null;
  birthYear: number | null;
};

const HOLDINGS_WIDGET_OPTIONS = [
  { label: 'Largest', value: 'largest' },
  { label: 'Movers', value: 'movers' },
] as const;

function getChangeTone(value: number | null): string {
  if (value == null) return 'text-gray-500 dark:text-gray-400';
  if (value > 0) return 'text-forest-600 dark:text-green-500';
  if (value < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-500 dark:text-gray-400';
}

export default function DashboardScreen() {
  const router = useRouter();
  const isDesktop = useIsDesktop();
  const c = useColors();
  const [refreshing, setRefreshing] = useState(false);
  const [holdingsWidgetView, setHoldingsWidgetView] = useState<HoldingsWidgetView>('largest');

  const accountsQ = trpc.accounts.list.useQuery();
  const assetsQ = trpc.assets.list.useQuery();
  const liabilitySummaryQ = trpc.liabilities.summary.useQuery();
  const holdingsQ = trpc.holdings.list.useQuery();
  const firePlanQ = trpc.fire.getPlan.useQuery();
  const profilesQ = trpc.profiles.list.useQuery();
  const transactionsQ = trpc.transactions.list.useQuery({ page: 1, pageSize: 5 });
  const reviewCountQ = trpc.transactions.reviewCount.useQuery();
  const perDiemQ = trpc.budget.perDiem.useQuery();
  const monthlySummaryQ = trpc.budget.monthlyDiscretionarySummary.useQuery();

  const isLoading =
    (accountsQ.isLoading && accountsQ.data == null)
    && (assetsQ.isLoading && assetsQ.data == null)
    && (profilesQ.isLoading && profilesQ.data == null);
  useScreenReadyTelemetry({
    ready: !isLoading,
    screenName: 'dashboard',
  });
  const error =
    accountsQ.error ??
    assetsQ.error ??
    profilesQ.error;

  const lifecycleQ = trpc.fire.getLifecycle.useQuery(undefined, { refetchInterval: 30000 });
  const accounts = accountsQ.data ?? [];
  const assets = assetsQ.data ?? [];
  const liabilitySummary = liabilitySummaryQ.data;
  const holdingsPortfolio = holdingsQ.data;
  const holdings = holdingsPortfolio?.items ?? [];
  const holdingsSummary = holdingsPortfolio?.summary;
  const firePlan = firePlanQ.data;
  const profiles = (profilesQ.data ?? []) as DashboardProfile[];
  const currentProfile = profiles.find((profile) => profile.isSelf) ?? profiles[0];
  const primaryProfile = profiles.find((p) => p.type === 'primary') ?? currentProfile;
  const spouseProfile = profiles.find((p) => p.type === 'spouse');
  const transactions = transactionsQ.data?.items ?? [];
  const reviewCount = reviewCountQ.data?.count ?? 0;
  const perDiem = perDiemQ.data;

  // Derived values
  const depositoryAccounts = accounts.filter(
    (a) => a.type === 'depository' && Number(a.balance) > 0,
  );
  const connectedNetWorth = accounts.reduce((sum, a) => {
    const bal = Number(a.balance);
    return a.type === 'credit' || a.type === 'loan' ? sum - bal : sum + bal;
  }, 0);
  const totalTrackedAssetValue = assets.reduce((sum, asset) => sum + (asset.estimatedValue ?? 0), 0);
  const totalManualLiabilities = assets.reduce(
    (sum, asset) =>
      sum + asset.manualLiabilities.reduce((debtSum, liability) => debtSum + liability.currentBalance, 0),
    0,
  );
  const totalNetWorth = connectedNetWorth + totalTrackedAssetValue - totalManualLiabilities;
  const liquidAssets = depositoryAccounts.reduce((sum, a) => sum + Number(a.balance), 0);

  const fiYear = firePlan?.beginYear ?? null;
  const primaryBirthYear = primaryProfile?.birthYear ?? null;
  const spouseBirthYear = spouseProfile?.birthYear ?? null;
  const primaryFiAge = primaryBirthYear && fiYear ? fiYear - primaryBirthYear : null;
  const spouseFiAge = spouseBirthYear && fiYear ? fiYear - spouseBirthYear : null;
  const fiAgeHeroCopy = formatFiAgeSummary({
    fiYear,
    primaryName: primaryProfile?.firstName ?? null,
    primaryFiAge,
    spouseName: spouseProfile?.firstName ?? null,
    spouseFiAge,
  });

  // Today's net worth change is effectively the portfolio's day move
  // (cash/real-estate don't move intraday). Source from holdings summary.
  const netWorthDayChangeAmount = holdingsSummary?.dayChangeAmount ?? null;
  const netWorthDayChangePercent =
    netWorthDayChangeAmount != null && totalNetWorth !== 0
      ? netWorthDayChangeAmount / (totalNetWorth - netWorthDayChangeAmount)
      : null;

  // Monthly spend vs budget — matches the Budget page's discretionary summary.
  const monthlySummary = monthlySummaryQ.data;
  const monthlySpend = monthlySummary?.actual ?? null;
  const monthlyBudget = monthlySummary?.budgeted ?? null;

  const holdingsWidgetItems = useMemo(() => {
    const dailyMarketHoldings = filterDailyMarketHoldings(holdings);
    const bySymbol = aggregatePortfolioHoldingsBySymbol(dailyMarketHoldings);
    return sortPortfolioHoldingAggregates(bySymbol, holdingsWidgetView).slice(0, 5);
  }, [holdings, holdingsWidgetView]);

  const refetchAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.allSettled([
      accountsQ.refetch(),
      assetsQ.refetch(),
      liabilitySummaryQ.refetch(),
      holdingsQ.refetch(),
      firePlanQ.refetch(),
      transactionsQ.refetch(),
      reviewCountQ.refetch(),
      perDiemQ.refetch(),
      monthlySummaryQ.refetch(),
    ]);
    setRefreshing(false);
  }, [accountsQ, assetsQ, liabilitySummaryQ, holdingsQ, firePlanQ, transactionsQ, reviewCountQ, perDiemQ, monthlySummaryQ]);

  const fireImpact = useFireImpact({ perDiem, firePlan, accounts });

  if (isLoading) return <LoadingSpinner />;
  if (error) {
    return (
      <ErrorState
        error={error}
        screen="dashboard"
        metadata={{ failedQueries: 'accounts.list,assets.list,profiles.list' }}
        onRetry={refetchAll}
      />
    );
  }

  return (
    <>
      <WebHead><title>Dashboard – Per Diem</title></WebHead>
      <ScrollView
        className="flex-1 bg-canvas dark:bg-canvas-dark"
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={refetchAll} tintColor={c.primary} />
      }
    >
      {/* Connection error banner — auto-shown if any connection needs re-auth */}
      <ConnectionErrorBanner onReconnected={refetchAll} />

      <PageContainer className={isDesktop ? '' : 'p-4'} size="wide">
        <DashboardGreeting firstName={currentProfile?.firstName ?? null} />
        {!isDesktop && <PageHeader title="Dashboard" subtitle="Your financial overview" />}
        {lifecycleQ.data && usesRetirementConfidence(lifecycleQ.data) && <FireLifecycleCard />}
        <View className="gap-4">
          {/* Per Diem Hero + Stats row — side-by-side on web */}
          <View className={isDesktop ? 'flex-row gap-4' : 'gap-4'}>
            <Pressable
              onPress={() => router.push('/(app)/budget')}
              className={isDesktop ? 'flex-1' : ''}
            >
              <PerDiemCard
                perDiem={perDiem}
                fireImpact={fireImpact}
              />
            </Pressable>

            {/* FIRE Plan teaser — beside hero on web, below hero on mobile */}
            <Pressable
              key={firePlan ? 'fire-plan-ready' : 'fire-plan-empty'}
              onPress={() => router.push('/(app)/fire-plan')}
              className={isDesktop ? 'flex-1' : ''}
            >
              <View
                className={`rounded-[18px] overflow-hidden shadow-card bg-[#1A1C1B] p-7${isDesktop ? ' h-full' : ''}`}
              >
                <Text className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-white/30">
                  FIRE Plan
                </Text>
                <Text className="text-[26px] font-bold text-white mt-3 leading-[1.1]">
                  {lifecycleQ.data?.enabled && lifecycleTitle(lifecycleQ.data) || (firePlan && fiYear ? fiAgeHeroCopy : 'Calculate your plan')}
                </Text>
                <Text className="text-[13px] text-white/35 mt-2">
                  {firePlan
                    ? 'Year-by-year projections →'
                    : 'See when you can retire based on your actual data →'}
                </Text>
              </View>
            </Pressable>
          </View>

          {/* Stat cards — single row on desktop, 2-up + 1-full on mobile */}
          {(() => {
            const netWorthCard = (
              <StatCard
                label="Net Worth"
                value={formatCurrency(totalNetWorth, { decimals: 0 })}
                subtitle="accounts + tracked assets"
                layout={isDesktop ? 'row' : 'full'}
                footerPlacement={isDesktop ? 'bottom' : 'aside'}
                onPress={() => router.push('/(app)/accounts')}
                footer={
                  netWorthDayChangeAmount != null ? (
                    <Money
                      weight={500}
                      className={`text-xs ${getChangeTone(netWorthDayChangeAmount)}`}
                      numberOfLines={1}
                      adjustsFontSizeToFit
                      minimumFontScale={0.8}
                    >
                      {formatCurrencyChange(netWorthDayChangeAmount)}
                      {netWorthDayChangePercent != null
                        ? ` (${formatSignedPercent(netWorthDayChangePercent, 2)})`
                        : ''}{' '}
                      today
                    </Money>
                  ) : null
                }
              />
            );
            const monthlySpendCard = (
              <StatCard
                label="Monthly Spend"
                value={monthlySpend != null ? formatCurrency(monthlySpend, { compact: true, decimals: 0 }) : '—'}
                subtitle={
                  monthlyBudget && monthlyBudget > 0
                    ? `of ${formatCurrency(monthlyBudget, { compact: true, decimals: 0 })} budget`
                    : 'no budget set'
                }
                valueColor={
                  monthlySpend != null && monthlyBudget && monthlySpend > monthlyBudget
                    ? 'text-red-600'
                    : 'text-forest-600 dark:text-green-500'
                }
                onPress={() => router.push('/(app)/budget')}
              />
            );
            const liquidAssetsCard = (
              <StatCard
                label="Liquid Assets"
                value={formatCurrency(liquidAssets, { compact: true, decimals: 0 })}
                subtitle="cash & checking"
                onPress={() => router.push('/(app)/accounts')}
              />
            );

            if (isDesktop) {
              return (
                <View className="flex-row gap-3">
                  {netWorthCard}
                  {monthlySpendCard}
                  {liquidAssetsCard}
                </View>
              );
            }

            return (
              <View className="gap-3">
                <View className="flex-row gap-3">
                  {liquidAssetsCard}
                  {monthlySpendCard}
                </View>
                {netWorthCard}
              </View>
            );
          })()}

          {/* Two-column layout on desktop for secondary cards */}
          <View className={isDesktop ? 'flex-row gap-4' : 'gap-4'}>
            {/* Left column */}
            <View className={`gap-4 ${isDesktop ? 'flex-1' : ''}`}>
              {/* Holdings widget */}
              {holdingsWidgetItems.length > 0 && holdingsSummary && (
                <Card>
                  <View className="flex-row items-start justify-between gap-3">
                    <View className="flex-1">
                      <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">Portfolio</Text>
                      <Text className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        <Money>{formatCurrency(holdingsSummary.totalValue, { compact: true, decimals: 0 })}</Money> across{' '}
                        {holdingsSummary.holdingsCount} position{holdingsSummary.holdingsCount === 1 ? '' : 's'}
                      </Text>
                    </View>
                    <Pressable onPress={() => router.push('/(app)/holdings')}>
                      <Text className="text-sm font-medium text-forest-600 dark:text-forest-500">Open portfolio</Text>
                    </Pressable>
                  </View>

                  <View className="mt-3">
                    <SegmentedControl
                      options={HOLDINGS_WIDGET_OPTIONS}
                      value={holdingsWidgetView}
                      onChange={setHoldingsWidgetView}
                    />
                  </View>

                  <View className="mt-3 gap-1">
                    {holdingsWidgetItems.map((holding) => {
                      const dailyChangeLabel = holding.dayChangeAmount != null
                        ? formatCurrencyChange(holding.dayChangeAmount)
                        : null;
                      const dailyChangePercentLabel = formatSignedPercent(holding.dayChangePercent);
                      const pricePerShare = holding.representative.pricePerShare;

                      return (
                        <Pressable
                          key={holding.key}
                          className="web-row-hover rounded-xl px-3 py-2.5"
                          onPress={() => router.push('/(app)/holdings')}
                        >
                          <View className="flex-row items-center justify-between gap-3">
                            <View className="flex-1">
                              <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {holding.symbol ?? holding.name}
                              </Text>
                              {pricePerShare != null ? (
                                <Money className={`mt-0.5 text-xs ${getChangeTone(holding.dayChangeAmount)}`} numberOfLines={1}>
                                  {formatSharePrice(pricePerShare)}/sh
                                  {holding.priceStatus !== 'current' ? ' · delayed' : ''}
                                </Money>
                              ) : (
                                <Text className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" numberOfLines={1}>
                                  {holding.name}{holding.priceStatus !== 'current' ? ' · delayed' : ''}
                                </Text>
                              )}
                            </View>
                            <View className="items-end">
                              <Money weight={500} className="text-sm text-gray-900 dark:text-gray-100">
                                {holding.totalValue != null ? formatCurrency(holding.totalValue, { decimals: 0 }) : '—'}
                              </Money>
                              {dailyChangeLabel && dailyChangePercentLabel ? (
                                <Money className={`mt-0.5 text-xs ${getChangeTone(holding.dayChangeAmount)}`}>
                                  {dailyChangeLabel} ({dailyChangePercentLabel})
                                </Money>
                              ) : (
                                <Text className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                  {holding.priceStatus === 'current' ? 'No daily move' : 'Delayed pricing'}
                                </Text>
                              )}
                            </View>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                </Card>
              )}

              {/* Cash Accounts */}
              {depositoryAccounts.length > 0 && (
                <Card>
                  <SectionHeader title="Cash Accounts" />
                  {depositoryAccounts.map((a) => (
                    <Pressable
                      key={a.id}
                      onPress={() =>
                        router.push({
                          pathname: '/(app)/transactions',
                          params: { accountId: a.id, accountName: a.name },
                        })
                      }
                      className="web-row-hover flex-row justify-between items-center py-2 border-b border-gray-50 dark:border-gray-700"
                    >
                      <View>
                        <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{a.name}</Text>
                        {a.mask && <Text className="text-xs text-gray-400">••••{a.mask}</Text>}
                      </View>
                      <Money weight={500} className="text-sm text-gray-800 dark:text-gray-200">
                        {formatCurrency(Number(a.balance))}
                      </Money>
                    </Pressable>
                  ))}
                </Card>
              )}
            </View>

            {/* Right column */}
            <View className={`gap-4 ${isDesktop ? 'flex-1' : ''}`}>
              {/* Needs-review prompt — jumps straight into the filtered list */}
              {reviewCount > 0 && (
                <Pressable
                  onPress={() =>
                    router.push({
                      pathname: '/(app)/transactions',
                      params: { needsReview: '1' },
                    })
                  }
                >
                  <Card className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
                    <View className="flex-row items-center gap-3">
                      <View className="h-10 w-10 rounded-full bg-amber-200 dark:bg-amber-800 items-center justify-center">
                        <Text className="text-base font-semibold text-amber-800 dark:text-amber-200">
                          {reviewCount > 99 ? '99+' : reviewCount}
                        </Text>
                      </View>
                      <View className="flex-1">
                        <Text className="text-base font-semibold text-amber-900 dark:text-amber-100">
                          {reviewCount === 1 ? 'Transaction needs review' : 'Transactions need review'}
                        </Text>
                        <Text className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                          Confirm categories to keep your per diem accurate →
                        </Text>
                      </View>
                    </View>
                  </Card>
                </Pressable>
              )}

              {/* Recent Transactions */}
              <Card>
                <SectionHeader
                  title="Recent Transactions"
                  action={
                    <Pressable onPress={() => router.push('/(app)/transactions')}>
                      <Text className="text-sm text-forest-600 dark:text-forest-500 font-medium">See all</Text>
                    </Pressable>
                  }
                />
                {transactions.length === 0 ? (
                  <Text className="text-sm text-gray-400 text-center py-4">No transactions yet</Text>
                ) : (
                  transactions.map((tx) => (
                    <Pressable
                      key={tx.id}
                      onPress={() => router.push('/(app)/transactions')}
                      className="web-row-hover flex-row justify-between items-center py-2.5 border-b border-gray-50 dark:border-gray-700"
                    >
                      <View className="flex-1 pr-2">
                        <Text className="text-sm font-medium text-gray-800 dark:text-gray-200" numberOfLines={1}>
                          {tx.name}
                        </Text>
                        <Text className="text-xs text-gray-400">{formatDate(tx.date, 'short')}</Text>
                      </View>
                      <Money weight={500} className="text-sm text-gray-800 dark:text-gray-200">
                        {formatCurrency(Math.abs(Number(tx.amount)))}
                      </Money>
                    </Pressable>
                  ))
                )}
              </Card>

            </View>
          </View>
        </View>
      </PageContainer>
      </ScrollView>
    </>
  );
}
